import React, { useEffect, useState } from 'react';
import { Card, Grid, Box, Skeleton, Typography } from '@mui/material';
import SummaryCommonCard from 'src/components/reports/summary-cards/summaryCommonCard';
import DonutChart from 'src/components/reports/summary-cards/summaryDonutCard';
import ReportHttpServices from 'src/services/reportsHttpService';
import { internalReportModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { useLocation } from 'react-router-dom';
import NoReportCard from 'src/components/reports/summary-cards/noReportCard';

const AudienceSummaryComponent = ({ 
    connectedProfileId, 
    startDate, 
    endDate, 
    viewBy, 
    audienceSummary, 
    serviceName 
}) => { 
    const [isLoading, setLoading] = useState(true);
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const urlBrandId = urlParams.get('brand_id');
    const urlToken = urlParams.get('token');

    const [summaryData, setSummaryData] = useState([]);
    const { showToast } = useToaster();

    useEffect(() => {
        if (connectedProfileId) {
            getAudienceSummary();
        }
    }, [connectedProfileId, startDate, endDate]);

    const getAudienceSummary = () => {
        setLoading(true);
        let body = {
            connected_profile_id: connectedProfileId,
            report_id: audienceSummary.id,
            date_range: {
                from: startDate,
                to: endDate,
            },
            view_by: "day"
        };
        let getProps = {
            url: `${internalReportModuleURL.scheduledReports}${serviceName}`,
            body: body,
            brandId: urlBrandId,
            token: urlToken,
            successCallback,
            failureCallback
        };
        ReportHttpServices.Post(getProps);
    };

    const failureCallback = (message) => {
        setLoading(false);
        showToast(message, "e");
        setSummaryData([]);
    };

    const successCallback = (data) => {
        const responseData = Array.isArray(data[0]?.data) ? data[0]?.data : [];
        setSummaryData(responseData);
        setLoading(false);
    };
    const renderComponent = (data) => {
        const commonGridProps = {
            item: true,
            xs: 12,
            sm: 6,
            key: data.id
        };

        const cardStyle = {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)'
            }
        };

        switch (data.card_type) {
            case "square":
                return (
                    <Grid {...commonGridProps} md={3} lg={2.4}>
                        <Box sx={cardStyle}>
                            <SummaryCommonCard
                                title={data?.title}
                                data={data?.data}
                                percentage={data?.percentage}
                                percentageState={data?.percentage_state}
                                averageData={data?.average_data}
                            />
                        </Box>
                    </Grid>
                );
            case "donut":
                return (
                    <Grid {...commonGridProps} md={6} lg={4.8}>
                        <Box sx={cardStyle}>
                            <DonutChart
                                title={data?.title}
                                value={data?.value}
                                labels={data?.key}
                            />
                        </Box>
                    </Grid>
                );
            case "no-report":
                return (
                    <Grid {...commonGridProps} md={3} lg={2.4}>
                        <Box sx={cardStyle}>
                            <NoReportCard />
                        </Box>
                    </Grid>
                );
            default:
                return null;
        }
    };

    const renderLoadingSkeleton = () => (
        <Grid container spacing={3} sx={{ p: 3 }}>
            {Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} lg={2.4} key={index}>
                    <Box
                        sx={{
                            height: '200px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <Skeleton 
                            variant="rectangular" 
                            width="100%" 
                            height="100%" 
                            animation="wave"
                            sx={{ borderRadius: '12px' }}
                        />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );

    const renderEmptyState = () => (
        <Grid container spacing={3} sx={{ p: 3 }}>
            {Array.from({ length: 4 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} lg={2.4} key={index}>
                    <Box
                        sx={{
                            height: '100%',
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}
                    >
                        <NoReportCard />
                    </Box>
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Card 
            elevation={0}
            sx={{ 
                borderRadius: '16px',
                border: '1px solid #f0f0f0',
                backgroundColor: '#fafafa',
                overflow: 'hidden'
            }}
        >
            {isLoading ? (
                renderLoadingSkeleton()
            ) : (
                <>
                    {summaryData?.length > 0 ? (
                        <Box sx={{ p: 3 }}>
                            <Grid container spacing={3} alignItems="stretch">
                                {summaryData?.map((data) => renderComponent(data))}
                            </Grid>
                        </Box>
                    ) : (
                        renderEmptyState()
                    )}
                </>
            )}
        </Card>
    );
};

export default AudienceSummaryComponent;
